import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { Button, Modal, Space, Typography } from 'antd';
import { LoadingState, useLoading } from '@monorepo/react-components';
import { useNotification } from '../../../hooks/use-notification';
import { useStore } from '../../../helpers/use-store';
import {
  WarningOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

export interface UpdateSeriesStatusModalActions {
  open: (seriesId: string, currentlyActive: boolean) => void;
}

const UpdateSeriesStatus = forwardRef<UpdateSeriesStatusModalActions>(
  (_, ref) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [seriesId, setSeriesId] = useState<string | null>(null);
    const [isCurrentlyActive, setIsCurrentlyActive] = useState<boolean>(false);
    const { loadingState, updateLoadingState } = useLoading();
    const {
      dataStore: { seriesStore },
    } = useStore();
    const notification = useNotification();

    useImperativeHandle(ref, () => ({
      open: (id: string, currentlyActive: boolean) => {
        setSeriesId(id);
        setIsCurrentlyActive(currentlyActive);
        setIsOpen(true);
      },
    }));

    const handleToggle = async () => {
      if (!seriesId) return;

      const newIsActive = !isCurrentlyActive;

      try {
        updateLoadingState(LoadingState.Loading);
        await seriesStore.update(seriesId, {
          isActive: newIsActive,
        });

        notification.success({
          message: t('common.success'),
          description: newIsActive
            ? t('series.status.activateSuccess')
            : t('series.status.disableSuccess'),
        });

        handleCancel();
      } catch (error) {
        console.error(
          `Failed ${newIsActive ? 'activating' : 'disabling'} series:`,
          error
        );
        notification.error({
          message: t('common.error'),
          description: newIsActive
            ? t('series.status.activateError')
            : t('series.status.disableError'),
        });
      } finally {
        updateLoadingState(LoadingState.Loaded);
      }
    };

    const handleCancel = () => {
      setIsOpen(false);
      setSeriesId(null);
    };

    const getModalContent = () => {
      if (isCurrentlyActive) {
        return {
          title: t('series.status.disableTitle'),
          icon: (
            <PauseCircleOutlined
              style={{ color: '#faad14', fontSize: '16px' }}
            />
          ),
          message: t('series.status.disableMessage'),
          buttonText: t('series.status.disableButton'),
          consequences: [
            t('series.status.disableConsequence1'),
            t('series.status.disableConsequence2'),
            t('series.status.disableConsequence3'),
            t('series.status.disableConsequence4'),
          ],
        };
      }
      return {
        title: t('series.status.enableTitle'),
        icon: (
          <PlayCircleOutlined style={{ color: '#52c41a', fontSize: '16px' }} />
        ),
        message: t('series.status.enableMessage'),
        buttonText: t('series.status.enableButton'),
        consequences: [
          t('series.status.enableConsequence1'),
          t('series.status.enableConsequence2'),
          t('series.status.enableConsequence3'),
        ],
      };
    };

    const content = getModalContent();

    return (
      <Modal
        title={content.title}
        open={isOpen}
        onCancel={handleCancel}
        footer={[
          <Button key="cancel" onClick={handleCancel}>
            Cancel
          </Button>,
          <Button
            key="toggle"
            type="primary"
            danger={isCurrentlyActive}
            loading={loadingState === LoadingState.Loading}
            onClick={handleToggle}
          >
            {content.buttonText}
          </Button>,
        ]}
      >
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Typography.Text strong style={{ fontSize: '16px' }}>
            {content.message}
          </Typography.Text>
          <div
            style={{
              background: isCurrentlyActive ? '#fffbe6' : '#f6ffed',
              border: `1px solid ${isCurrentlyActive ? '#ffe58f' : '#b7eb8f'}`,
              borderRadius: '6px',
              padding: '12px',
            }}
          >
            <Space align="start">
              {content.icon}
              <div>
                <Typography.Text
                  strong
                  style={{
                    color: isCurrentlyActive ? '#d48806' : '#389e0d',
                    display: 'block',
                    marginBottom: '4px',
                  }}
                >
                  {t('series.status.thisWill')}
                </Typography.Text>
                <ul style={{ margin: '0', paddingLeft: '16px', color: '#666' }}>
                  {content.consequences.map((consequence, index) => (
                    <li key={index}>{consequence}</li>
                  ))}
                </ul>
              </div>
            </Space>
          </div>
        </Space>
      </Modal>
    );
  }
);

export default UpdateSeriesStatus;
